import { useEffect, useState } from "react";
import { useAppState } from "../../app-state";
import type { LaunchDraftInput } from "../../lib/launchpad";
import { workspaceRequest } from "../../lib/workspace";

export function FeePresets({ onChange, disabled = false, showGuidance = true }: { onChange: (rate: number) => void; disabled?: boolean; showGuidance?: boolean }) {
  const { fees } = useAppState();
  return (
    <div className="fee-options">
      <div className="fee-presets" role="group" aria-label="Confirmation speed">
        {fees ? (
          <>
            <button
              type="button" disabled={disabled}
              onClick={() => onChange(Math.max(0.1, fees.economyFee))}
            >
              ECONOMY
            </button>
            <button
              type="button" disabled={disabled}
              onClick={() => onChange(Math.max(0.1, fees.halfHourFee))}
            >
              ~30 MIN
            </button>
            <button
              type="button" disabled={disabled}
              onClick={() => onChange(Math.max(0.1, fees.fastestFee))}
            >
              ~10 MIN
            </button>
          </>
        ) : null}
      </div>
      {showGuidance ? <details className="fee-guidance">
        <summary>Estimated times · About fees</summary>
        <p>sat/vB is the network fee per unit of transaction size. Confirmation times are estimates and change with congestion.</p>
      </details> : null}
    </div>
  );
}
export function InscriptionHelp() {
  return (
    <details className="experience-help inscription-help">
      <summary>HOW INSCRIBING WORKS</summary>
      <p>
        1. Choose your content and recipient. 2. Review the calculated price.
        3. Your wallet signs the payment (commit) and inscription
        (reveal).
      </p>
      <p>
        <strong>Hive:</strong> inscribes a reference used to load uploaded
        media. <strong>Delegate:</strong> references an existing inscription's
        content.
      </p>
      <p>
        <strong>Postage:</strong> sats carried with your inscription.{" "}
        <strong>Prepare only:</strong> signs and downloads the transaction
        package without broadcasting it. Keep the downloaded package safe.
      </p>
    </details>
  );
}
export function LaunchReadiness({
  draft,
  supply,
}: {
  draft: LaunchDraftInput;
  supply: number;
}) {
  const { authorization, authorizeWallet } = useAppState();
  const [checks, setChecks] = useState<
    { label: string; ok: boolean; detail: string }[]
  >([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    setChecks([]);
  }, [draft, supply]);
  const check = async () => {
    setBusy(true);
    setError("");
    try {
      const token = authorization ?? (await authorizeWallet());
      if (!token) return;
      const result = await workspaceRequest<{ checks: typeof checks }>(
        token,
        "/readiness",
        { draft, supply },
      );
      setChecks(result.checks);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Readiness check failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="experience-help">
      <h3>LAUNCH READINESS</h3>
      <p>
        Check metadata, inventory, sale stages and payout amounts before
        publication. Live ownership and funding are revalidated by the
        publishing and minting flows.
      </p>
      <button
        className="lp-secondary"
        type="button"
        disabled={busy}
        onClick={() => void check()}
      >
        {busy ? "CHECKING…" : "CHECK READINESS"}
      </button>
      {error ? <p role="alert">{error}</p> : null}
      <ul className="readiness-list">
        {checks.map((c) => (
          <li key={c.label}>
            <strong>
              {c.ok ? "✓" : "!"} {c.label}
            </strong>
            <p>{c.detail}</p>
          </li>
        ))}
      </ul>
    </section>
  );
}
